import { useEffect, useMemo, useState, type FC, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { ScrollProvider } from "./contexts/ScrollContext";
import storageService, { type PageStorageScope } from "./core/services/storageService";
import ChatToggleButton from "./components/ChatToggleButton";
import ChatWidget from "./components/ChatWidget";
import { TranslationProvider } from "./contexts/TranslationContext";
import { WidgetProvider, WidgetContextType } from "./contexts/WidgetContext";
import { SupportAgentsProvider } from "./contexts/SupportAgentsContext";
import { User } from "./core/types";
import { setupWindowMessageListener } from "./utils/messageListenerUtil";
import { acquireScrollLock, releaseScrollLock } from "./utils/scrollLock";
import { getSelectedUrlParams } from "./utils/urlUtil";
import { resolveInitialOpenState } from "./utils/initialOpenState";
import { LastBotCore } from "./core/LastBotCore";
import { useCoreState } from "./contexts/useCoreState";
import { CoreProvider } from "./contexts/CoreContext";
import { useThreadPagePersistence } from "./contexts/useThreadPagePersistence";
import { Templated } from "./templating";
import type { LastBotState, WidgetConfig } from "./core/state";
import type { BrandInformation, Translations } from "./core/types";
import { RichMediaProvider } from "./contexts/RichMediaContext";

const agentsSelector = (state: LastBotState): User[] => state.agents.online;
const translationsSelector = (state: LastBotState): Translations => state.translations;
const brandSelector = (state: LastBotState): BrandInformation | null => state.brand;
const widgetConfigSelector = (state: LastBotState): WidgetConfig | null => state.widget;
const threadSelector = (state: LastBotState): LastBotState["thread"] => state.thread;

interface AppProps {
  core: LastBotCore;
  autoOpen: boolean;
  fullscreen: boolean;
  widgetId: string;
  /** Optional embed-placement identity. Effective placement key = placementId || widgetId. */
  placementId?: string;
  startMessage: string;
  userEmail: string;
  hideToggleButton: boolean;
  homeUrl?: string;
  skipIngest?: boolean;
  hubIdentity?: string;
  /** When true, chat is always visible and fills its (already inline-mounted) host. No toggle button. */
  inline?: boolean;
  /**
   * True when the host page declared `<template id="lastbot-custom-main-page">`. Set
   * by `widget.tsx` after `discoverTemplates(document)`. Flips ChatWidget routing
   * to render `CustomMainPage` for `landing | customerInquiry | conversation`.
   */
  hasCustomMainPage?: boolean;
}

export const isValidEmail = (value: string): boolean => {
  // Explanation of the regex:
  // ^             -> Start of the string
  // [^\s@]+       -> One or more characters that are NOT spaces or '@' (ensures it doesn't start with '@')
  // @             -> Must contain exactly one '@'
  // [^\s@]+       -> One or more characters after '@' (ensures there is a domain name)
  // \.            -> A literal dot '.' (ensures there is a dot after the domain)
  // [a-zA-Z]{2,}$ -> Ensures at least **two** alphabetic characters after the last dot (e.g., ".com", ".org")
  return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(value.trim());
};

const isValidConfig = (userEmail: string): boolean => {
  // Notice that the user email is optional, so we only validate it if it's provided
  if (userEmail && !isValidEmail(userEmail)) {
    console.error(`[lastbot-chat] The configuration value for "user-email" is not a valid email address.`);
    return false;
  }

  return true;
};

const App: FC<AppProps> = ({
  core,
  autoOpen,
  fullscreen,
  widgetId,
  placementId,
  startMessage,
  userEmail,
  hideToggleButton,
  homeUrl,
  skipIngest,
  hubIdentity,
  inline = false,
  hasCustomMainPage = false,
}) => {
  // Effective per-placement key: the explicit placement-id, or the widget-id as a
  // backward-compatible fallback. Used to target this placement via postMessage /
  // getCore(). (Persisted UI state is scoped separately — see storageScope below.)
  const placementKey = placementId || widgetId;

  // Storage scope for placement-local persisted UI state. Composite
  // `<widgetId>:<placementId>` keying (see PageStorageScope) — NOT the same as
  // placementKey above.
  const storageScope = useMemo<PageStorageScope>(
    () => ({ widgetId, placementId: placementId || undefined }),
    [widgetId, placementId],
  );

  // Persist the active thread into scoped page storage for the whole widget
  // lifetime (panel open or closed): ChatWidget's rehydration hook
  // (custom-main) and ConversationPage's bootstrap (multi-page) read it back
  // after a reload. (LAS-1492)
  useThreadPagePersistence(core, storageScope);

  const [isOpen, setIsOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [initialized, setInitialized] = useState(false);
  // Explicit open/close intent observed via widget:open / widget:close. The
  // listeners attach at mount — BEFORE init() resolves — so an embedder (or
  // the postMessage relay) can legitimately call core.open() while init is
  // still in flight. initializeApp consults this so its auto-open defaults
  // never overwrite an explicit intent; without it a pre-ready open() was
  // silently clobbered and its panel-ready promise hung forever (LAS-2166
  // review). null = no intent received yet.
  const explicitOpenIntentRef = useRef<boolean | null>(null);
  const supportAgents = useCoreState(core, agentsSelector);
  const translations = useCoreState(core, translationsSelector);
  const brand = useCoreState(core, brandSelector);
  const widgetConfig = useCoreState(core, widgetConfigSelector);
  const thread = useCoreState(core, threadSelector);
  // Opaque per-instance token for the shared, reference-counted scroll-lock manager. The
  // manager captures/restores the host page's <html> style once across all placements, so
  // concurrent fullscreen (reachable via core.toggleFullscreen() on multiple placements)
  // can't leave the page's scroll stuck. See src/utils/scrollLock.ts.
  const scrollLockToken = useRef({}).current;

  const widgetContext = useMemo<WidgetContextType | null>(() => {
    if (!initialized || !brand || !widgetConfig) return null;
    return {
      apiUrl: core.apiUrl,
      widgetId,
      storageScope,
      startMessage: startMessage.trim(),
      userEmail: userEmail.trim(),
      hubIdentity,
      brandInformation: brand,
      conversationStarters: widgetConfig.conversationStarters,
      averageResponseTime: widgetConfig.averageResponseTime,
      voiceModeAvailable: widgetConfig.voiceModeAvailable,
      attachmentsEnabled: widgetConfig.attachmentsEnabled,
      hideToggleButton,
      homeUrl,
      skipIngest,
      theme: widgetConfig.theme,
      hasCustomMainPage,
    };
  }, [
    initialized,
    brand,
    widgetConfig,
    core,
    widgetId,
    storageScope,
    startMessage,
    userEmail,
    hubIdentity,
    hideToggleButton,
    homeUrl,
    skipIngest,
    hasCustomMainPage,
  ]);

  useEffect(() => {
    const initializeApp = async (
      autoOpenBool: boolean,
      fullscreenBool: boolean,
      trimmedStartMessage: string,
    ): Promise<void> => {
      const result = await core.init();
      if (!result.success) return;

      // Core has already populated state.brand / state.translations / state.widget /
      // state.contact and called storageService.setContact internally; here we only
      // handle UI-side concerns (session id, UTM capture, page seeding).
      const existingSessionId = storageService.getSessionId();
      storageService.setSessionId(existingSessionId || uuidv4());

      // UTM is page-level: capture once per page load (the first placement to initialise).
      if (existingSessionId.length === 0) {
        const utmParams = getSelectedUrlParams(["utm_source", "utm_campaign", "utm_medium", "utm_term", "utm_content"]);
        storageService.setUtmParams(utmParams);
      }

      // start-message seeding is PER-PLACEMENT, not page-level: seed this placement's
      // conversation page when it has a start message and hasn't navigated yet (its own
      // scoped page is still the default landing). Gating this on the shared session id
      // would seed only the first placement to initialise and silently skip the rest.
      if (trimmedStartMessage && storageService.getPage(storageScope).type === "landing") {
        storageService.setPage(storageScope, { type: "conversation" });
      }

      // In inline mode the chat starts open (autoOpen is overridden to true). The user can
      // still close via the navbar close button, which fires `widget:close` and renders the
      // floating toggle button as the re-open affordance.
      //
      // Defaults apply only when no explicit open()/close() intent arrived
      // while init was in flight — the intent wins, so a pre-ready open()
      // mounts ChatWidget once `initialized` flips and its panelInteractive
      // ack resolves the pending open() promise.
      //
      // A visitor's earlier close in this browser session suppresses auto-open,
      // so the chat does not reopen on every page load (LAS-4555).
      const initialState = resolveInitialOpenState({
        explicitIntent: explicitOpenIntentRef.current,
        inline,
        autoOpen: autoOpenBool,
        fullscreen: fullscreenBool,
        dismissed: storageService.isDismissed(storageScope),
        hideToggleButton,
      });
      setIsOpen(initialState.isOpen);
      setIsFullscreen(initialState.isFullscreen);

      // LastBotCore owns both the account channel and the message-thread channel; nothing else
      // needs initialization here. Brand/translations/widget config flow in via state.
      setInitialized(true);
    };

    if (isValidConfig(userEmail.trim())) {
      void initializeApp(autoOpen, fullscreen, startMessage.trim());
    }
    // The core's lifecycle (init/destroy) is owned by the custom element <lastbot-chat>;
    // App.tsx just observes its state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!initialized) {
      return;
    }
    // Hide/restore the host page scrollbar via the shared reference-counted manager, so
    // multiple concurrently-fullscreen placements don't clobber each other's lock. Release
    // of a token that never acquired is a no-op.
    if (isFullscreen) {
      acquireScrollLock(scrollLockToken);
    } else {
      releaseScrollLock(scrollLockToken);
    }
  }, [isFullscreen, initialized, scrollLockToken]);

  useEffect(() => {
    const handleStorageContactChange = (event: StorageEvent): void => {
      if (event.key === `LastbotChat_contact:${widgetId}`) {
        const contact = JSON.parse(event.newValue!);
        if (!contact || contact.email) {
          storageService.setContact(widgetId, contact);
        }
      }
    };

    // Listen for localStorage changes across tabs/windows
    window.addEventListener("storage", handleStorageContactChange);

    // Default UI subscribes to core open/close intents. Either an embedder calling
    // core.open() / core.close() OR the legacy postMessage path (relayed below) reaches
    // setIsOpen through a single code path.
    // Open and close also record the visitor's choice for later page loads in
    // this browser session: a close suppresses auto-open, an open lifts it.
    // Inline placements always start open, so they record nothing.
    const handleOpen = (): void => {
      explicitOpenIntentRef.current = true;
      if (!inline) storageService.setDismissed(storageScope, false);
      setIsOpen(true);
    };
    // Mirror handleOnClose: clear fullscreen so document scroll-lock is
    // released regardless of which path triggered the close (UI close button,
    // core.close(), postMessage relay).
    const handleClose = (): void => {
      explicitOpenIntentRef.current = false;
      if (!inline) storageService.setDismissed(storageScope, true);
      setIsOpen(false);
      setIsFullscreen(false);
    };
    // `core.toggleFullscreen()` is the v2 entry point — interactive slots and
    // host-page buttons go through it instead of prop-drilled onToggleFullscreen.
    const handleFullscreenToggle = (): void =>
      setIsFullscreen(prev => !prev);
    core.on("widget:open", handleOpen);
    core.on("widget:close", handleClose);
    core.on("widget:fullscreen-toggle", handleFullscreenToggle);

    // Legacy postMessage entry point (kept for backward compatibility — cross-frame and
    // pre-`lastbot:ready` callers still benefit from this). Routes through core.open()/close()
    // so visibility has a single source of truth.
    const cleanupMessageListener = setupWindowMessageListener({
      onOpenChat: () => {
        // Fire-and-forget: the postMessage relay has no caller to await the
        // panel-ready promise (LAS-2166), and open() pre-handles its own rejection.
        void core.open();
      },
      onCloseChat: () => core.close(),
      widgetId,
      placementKey,
    });

    return () => {
      // Release this placement's scroll-lock if it held one (unmounted while fullscreen).
      // The manager restores the host page's <html> style only when the last owner releases,
      // and ignores tokens that never acquired — so a non-fullscreen sibling unmounting can't
      // release another placement's active lock.
      releaseScrollLock(scrollLockToken);

      // When flushing iframe, window might not be available
      if (window) {
        // Clean up event listener for storage changes
        window.removeEventListener("storage", handleStorageContactChange);
      }

      // Clean up window message listener
      cleanupMessageListener();
      core.off("widget:open", handleOpen);
      core.off("widget:close", handleClose);
      core.off("widget:fullscreen-toggle", handleFullscreenToggle);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // `data-lastbot-state` — the widget's visibility/mode at the App level.
  // Carried on a persistent display:contents wrapper so it's always queryable
  // (even when the chat panel is closed and only the toggle button is visible,
  // or when both are suppressed). The wrapper layout is invisible thanks to
  // display:contents; descendants behave exactly as if it weren't there.
  const widgetOpen = isOpen;
  const widgetState = !widgetOpen
    ? hideToggleButton
      ? "hidden"
      : "closed"
    : isFullscreen
      ? "full-screen"
      : "open";

  return (
    <CoreProvider core={core}>
      <RichMediaProvider
        core={core}
        threadId={thread?.id ?? null}
        messages={thread?.messages ?? []}
        isOpen={isOpen}
        isFullscreen={isFullscreen}
        inline={inline}
      >
        {!initialized || !widgetContext ? null : (
          <WidgetProvider widgetContext={widgetContext}>
            <SupportAgentsProvider supportAgents={supportAgents}>
              <TranslationProvider translations={translations}>
                <ScrollProvider>
              {/*
                `data-lastbot-state` sits OUTSIDE the lastbot-app Templated
                so the attribute stays available to host-page CSS even when
                the embedder declares `<template id="lastbot-app">` (in
                which case Templated swaps its children for the embedder's
                tree).
              */}
              <div style={{ display: "contents" }} data-lastbot-state={widgetState}>
                {/*
                  Top-level Templated. When no embedder template named
                  "lastbot-app" is registered, this is a passthrough — the
                  widget renders exactly as before. When an embedder declares
                  `<template id="lastbot-app">`, the parsed embedder tree
                  renders instead. Interactive slots in the embedder's tree
                  (e.g. `<slot name="history-button"><button>…</button></slot>`)
                  get their onClick auto-attached by Templated's
                  `wireInteractiveFallback`, so a button declared by the
                  embedder fires `core.showPage("history")` (and the `history:click`
                  CustomEvent) without any widget content needing to be
                  relocated across React tree boundaries.

                  Display slots inside the embedder's lastbot-app template
                  render whatever fallback content the embedder declared —
                  widget-state-derived display content (brand-avatar URL,
                  per-message body, …) isn't currently projected across the
                  app boundary; embedders who need that should override the
                  individual page-level templates (lastbot-navbar, etc.)
                  where harvest still works.
                */}
                <Templated id="lastbot-app">
                  {inline ? (
                    isOpen ? (
                      <ChatWidget isFullscreen={false} inline />
                    ) : hideToggleButton ? null : (
                      <ChatToggleButton />
                    )
                  ) : isOpen ? (
                    <ChatWidget isFullscreen={isFullscreen} />
                  ) : hideToggleButton ? null : (
                    <ChatToggleButton />
                  )}
                </Templated>
              </div>
                </ScrollProvider>
              </TranslationProvider>
            </SupportAgentsProvider>
          </WidgetProvider>
        )}
      </RichMediaProvider>
    </CoreProvider>
  );
};

export default App;
